// Phase 7 — Track B T-B4. Auth form validation.
//
// Mirrors the backend's `changePasswordSchema` and `accountDeletionSchema`
// in `src/core/auth/routes.ts` (delivered in D1 / Track A).
//
// Q-3b: account deletion uses defense-in-depth — the user must re-enter the
// current password AND type the literal phrase "delete my account". The
// backend validator uses `z.literal(...)`; we mirror that here so the form
// surfaces the typo before the round-trip.
//
// MIN_PASSWORD_LENGTH is 12 (per `src/core/auth/passwords.ts`). The
// `changePasswordBody` schema gates `new_password` at 12+ to match. The
// `confirm_new_password` field is form-only — the backend never sees it —
// so it's NOT in `changePasswordBodySchema` (the backend body shape) but
// IS in the form schema; the form schema cross-checks the two fields.

import { z } from 'zod';

/** Same constant as backend `src/core/auth/passwords.ts`; duplicated here
 *  to keep `@dailydeck/shared` independent of backend imports. If the
 *  backend ever bumps the floor, update both. */
export const MIN_PASSWORD_LENGTH = 12;

/** Literal phrase the user must type to confirm account deletion (Q-3b). */
export const ACCOUNT_DELETION_PHRASE = 'delete my account';

/**
 * Backend body shape for `POST /api/auth/change-password`. The form sends
 * a superset (`confirm_new_password`) that we strip before posting.
 */
export const changePasswordBodySchema = z
  .object({
    current_password: z.string().min(1).max(1024),
    new_password: z.string().min(MIN_PASSWORD_LENGTH).max(1024),
  })
  .strict();

export type ChangePasswordBody = z.infer<typeof changePasswordBodySchema>;

/**
 * Form-side schema. Adds `confirm_new_password` and a cross-field check.
 * The form posts only `current_password` + `new_password` to the backend.
 */
export const changePasswordFormSchema = z
  .object({
    current_password: z.string().min(1).max(1024),
    new_password: z.string().min(MIN_PASSWORD_LENGTH).max(1024),
    confirm_new_password: z.string().min(1).max(1024),
  })
  .strict()
  .refine((v) => v.new_password === v.confirm_new_password, {
    message: 'passwords do not match',
    path: ['confirm_new_password'],
  });

export type ChangePasswordForm = z.infer<typeof changePasswordFormSchema>;

/**
 * Backend body shape for `DELETE /api/auth/account`. The literal phrase
 * uses `z.literal(...)` exactly like the server.
 */
export const accountDeletionBodySchema = z
  .object({
    current_password: z.string().min(1).max(1024),
    confirmation_phrase: z.literal(ACCOUNT_DELETION_PHRASE),
  })
  .strict();

export type AccountDeletionBody = z.infer<typeof accountDeletionBodySchema>;

/**
 * Phase 7.1 — register form schema. Mirrors the backend `registerSchema`
 * in `src/core/auth/routes.ts:67` for `email` + `password`, and adds an
 * explicit `passwordConfirm` field with a cross-field equality check.
 *
 * The backend never sees `passwordConfirm` — the SPA strips it before
 * posting `{ email, password }` (no `plan` is sent; the backend defaults
 * to 'personal' which is the v1 single-account plan).
 *
 * The `email` is trimmed + lowercased to mirror the login form's
 * normalisation (so a typo in case does not surface as a 409 mismatch).
 */
export const registerFormSchema = z
  .object({
    email: z.string().trim().toLowerCase().email().max(254),
    password: z.string().min(MIN_PASSWORD_LENGTH).max(1024),
    passwordConfirm: z.string().min(1).max(1024),
  })
  .strict()
  .refine((v) => v.password === v.passwordConfirm, {
    message: 'passwords do not match',
    path: ['passwordConfirm'],
  });

export type RegisterFormBody = z.infer<typeof registerFormSchema>;
